// The currency code always trails the number (`70,000 CZK`, `70 000 CZK`) regardless of locale;
// Intl's currency style would put it first in English and use `Kč` in Czech. Non-breaking space
// keeps the code on the same line as the amount.
const CZK_SUFFIX = '\u00a0CZK'

export const formatCzk = (value: number, locale: string): string =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value)}${CZK_SUFFIX}`

/** Short axis label, e.g. `1.2M CZK` / `1,2 mil. CZK`. */
export const formatCzkCompact = (value: number, locale: string): string =>
  `${new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value)}${CZK_SUFFIX}`

export const formatPercent = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)

export const formatKm = (value: number, locale: string): string =>
  `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} km`

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/

/**
 * Chart axes and tooltips call these with whatever label recharts has at hand, including
 * `undefined` while no tooltip is active. Formatting an invalid Date throws
 * (`RangeError: Invalid time value`), so anything that isn't a `YYYY-MM-DD` key passes through.
 */
export const formatDayKey = (dayKey: unknown, locale: string, options: Intl.DateTimeFormatOptions): string => {
  if (typeof dayKey !== 'string' || !DAY_KEY.test(dayKey)) return dayKey == null ? '' : String(dayKey)
  const date = new Date(`${dayKey}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return dayKey
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' }).format(date)
}

/** `6. 10.` style short day label for chart axes. */
export const formatShortDay = (dayKey: unknown, locale: string): string =>
  formatDayKey(dayKey, locale, { day: 'numeric', month: 'numeric' })

/** `po 6. 10.` style label including the weekday, for tooltips. */
export const formatWeekdayDay = (dayKey: unknown, locale: string): string =>
  formatDayKey(dayKey, locale, { weekday: 'short', day: 'numeric', month: 'numeric' })

/** Round a magnitude up to a "nice" number (1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8 × 10ⁿ) so axis ticks stay round. */
export const niceCeil = (value: number): number => {
  if (value <= 0) return 0
  const magnitude = 10 ** Math.floor(Math.log10(value))
  const step = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].find(factor => factor * magnitude >= value)!
  return step * magnitude
}
